import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function PaymentPaypalReturn() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const subId = params.get("subscription_id");
  const { user } = useAuth();
  const [status, setStatus] = useState("processing");
  const [error, setError] = useState("");
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    if (subId) {
      api
        .post(`/paypal/subscription/${subId}/confirm`)
        .then((r) => setStatus(["ACTIVE", "APPROVED"].includes(r.data.status) ? "paid" : "pending"))
        .catch((e) => {
          setStatus("error");
          setError(formatApiError(e));
        });
      return;
    }
    if (!token) {
      setStatus("error");
      setError("Parametro ordine mancante.");
      return;
    }
    api
      .post(`/paypal/order/${token}/capture`)
      .then((r) => setStatus(r.data.status === "COMPLETED" ? "paid" : "pending"))
      .catch((e) => {
        setStatus("error");
        setError(formatApiError(e));
      });
  }, [token]);

  return (
    <div data-testid="payment-paypal-return" className="min-h-screen grid place-items-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-md w-full text-center"
      >
        {status === "processing" && (
          <>
            <Loader2 size={40} className="animate-spin text-[#E05638] mx-auto" />
            <h1 className="font-display font-bold text-2xl mt-6">Conferma pagamento PayPal…</h1>
            <p className="text-neutral-500 text-sm mt-3">Stiamo verificando il pagamento con PayPal.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={48} className="text-emerald-500 mx-auto" />
            <h1 className="font-display font-bold text-3xl mt-6">Pagamento ricevuto!</h1>
            <p className="text-neutral-600 text-sm mt-3 leading-relaxed">
              Grazie! Abbiamo confermato il tuo pagamento PayPal per la creazione del Sito Business.
              Il team ti contatterà entro 24 ore per il kickoff del progetto.
            </p>
            <Link
              to={user ? "/dashboard" : "/accedi"}
              data-testid="paypal-success-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#111111] text-white px-7 py-3.5 text-sm font-semibold hover:bg-[#E05638] transition-colors"
            >
              {user ? "Vai alla tua area clienti" : "Accedi alla tua area clienti"} <ArrowRight size={15} />
            </Link>
          </>
        )}
        {status === "pending" && (
          <>
            <Loader2 size={40} className="text-neutral-400 mx-auto" />
            <h1 className="font-display font-bold text-2xl mt-6">Pagamento in elaborazione</h1>
            <p className="text-neutral-600 text-sm mt-3 leading-relaxed">
              PayPal sta ancora elaborando il pagamento. Riceverai conferma via email a breve.
            </p>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle size={48} className="text-[#E05638] mx-auto" />
            <h1 className="font-display font-bold text-2xl mt-6">Qualcosa è andato storto</h1>
            <p className="text-neutral-600 text-sm mt-3 leading-relaxed">
              {error || "Non siamo riusciti a confermare il pagamento."} Se l'addebito è avvenuto,
              scrivici a lumoratech.it@gmail.com e risolviamo subito.
            </p>
            <Link
              to="/"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#111111] text-white px-7 py-3.5 text-sm font-semibold hover:bg-[#E05638] transition-colors"
            >
              Torna alla home
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
